import { action, computed, makeObservable, observable } from 'mobx'

import {
  IPermitTypeField,
  PermitFieldType,
  WorkflowStepType,
} from '~/client/graph'
import FormReportGroupingOption from '~/client/src/desktop/enums/FormReportGroupingOption'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import {
  BasicDataKeys,
  ILWFCColumn,
  ILWFCRow,
  LWFCRowData,
} from '~/client/src/shared/components/ListWithFixedColumns/VirtualListHelpers'
import {
  companyPermitFieldTypes,
  isLinkedWorkflowLikeField,
  locationPermitFieldTypes,
  materialPermitFieldTypes,
  materialResourcePermitFieldTypes,
  userPermitFieldTypes,
} from '~/client/src/shared/constants/PermitFieldTypeConstants'
import {
  FORM_MATERIAL_FIELDS_KEY,
  FORM_MATERIAL_RESOURCE_ASSOCIATED_FIELDS_KEY,
  FORM_MATERIAL_RESOURCE_FIELDS_KEY,
  PERMIT_TABLE_FIELDS_KEY,
} from '~/client/src/shared/constants/permitTypeFieldsConstants'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import { getFormStatusDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import PermitField from '~/client/src/shared/models/PermitField'
import Workflow from '~/client/src/shared/models/Workflow'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import PermitInspectionChangesStore from '~/client/src/shared/stores/domain/PermitInspectionChanges.store'
import PermitInspectionsStore from '~/client/src/shared/stores/domain/PermitInspections.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import UIFilterInfo from '~/client/src/shared/stores/substates/UIFilterInfo'
import { ITreeNodeObj } from '~/client/src/shared/stores/ui/BaseList.store'
import BaseMultiBandListStore from '~/client/src/shared/stores/ui/BaseMultiBandList.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { groupingCommonMapper } from '~/client/src/shared/utils/mappers'
import {
  EMPTY_STRING,
  LOCATION_SEPARATOR,
  NO_SPECIFIED,
  NO_VALUE,
} from '~/client/src/shared/utils/usefulStrings'
import { copyObjectDeep } from '~/client/src/shared/utils/util'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import ReportsStore from '../../../Reports.store'

enum SpecificDataKeys {
  NAME = 'name',
  RECEIPT = 'receipt',
  STATUS = 'status',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  LOCATION = 'location',
  COMPANY = 'company',
  RESPONSIBLE = 'responsible',
  SUMMARIZED = 'summarized',
}

const DEFAULT_SORT_KEY = 'name'

export const DataKeys = { ...BasicDataKeys, ...SpecificDataKeys }
const FILTER_KEYS = ['id']
const DEFAULT_ROW_HEIGHT = 48
const MULTILINE_ROW_HEIGHT = 35
const CATEGORY_ROW_HEIGHT = 32
const SMALL_DYNAMIC_COLUMN_MIN_LENGTH = 50
const SMALL_DYNAMIC_COLUMN_WIDTH = 200
const BIG_DYNAMIC_COLUMN_WIDTH_MULTIPLIER = 150

enum AcceptableFormFilterTypes {
  COMPANY = 'company',
  STATUS = 'status',
  LOCATION = 'location',
}

interface DynamicColumn {
  label: string
  dataKey: string
  uiKey: string
  checkListItemId?: string
  isSummarized?: boolean
  parentFieldId?: string
  isDependentField?: boolean
  isParentField?: boolean
}

interface ParentFieldWithData {
  parentFieldId: string
  parentField: PermitField
  isTable: boolean
}

const unassignedValues = [UNASSIGNED, NO_SPECIFIED, EMPTY_STRING, NO_VALUE]
const checklistFormFieldTypes = [
  PermitFieldType.Question,
  PermitFieldType.Checklist,
]

export default class FormReportListStore extends BaseMultiBandListStore<Workflow> {
  private readonly collator = new Intl.Collator([], {
    numeric: true,
    sensitivity: 'accent',
  })

  public constructor(
    private readonly state: DesktopInitialState,
    private readonly reportsStore: ReportsStore,
    private readonly companiesStore: CompaniesStore,
    private readonly workflowTypesStore: WorkflowTypesStore,
    private readonly projectDateStore: ProjectDateStore,
    private readonly permitInspectionsStore: PermitInspectionsStore,
    private readonly permitInspectionChangesStore: PermitInspectionChangesStore,
    private readonly projectMembersStore: ProjectMembersStore,
    private readonly locationAttributesStore: LocationAttributesStore,
    private readonly materialsStore: MaterialsStore,
  ) {
    super(
      state.formReportFilters,
      () => reportsStore.allFormReportPermits,
      FILTER_KEYS,
    )
    makeObservable(this)
  }

  @observable.shallow
  private materialResourceRecords: IResourceRecord[] = []

  @computed
  private get materialResourceRecordsById(): Map<string, IResourceRecord> {
    const m = new Map<string, IResourceRecord>()
    for (const r of this.materialResourceRecords) m.set(String(r.id), r)
    return m
  }

  @action.bound
  public setMaterialResourceRecords(records: IResourceRecord[]) {
    this.materialResourceRecords = records ?? []
  }

  @computed
  public get activeFormType(): WorkflowType {
    return this.workflowTypesStore.actualTypes.find(
      actualType => actualType.id === this.reportsStore.activeReportView?.id,
    )
  }

  @computed
  public get activeFormTypeFieldsMap(): Map<string, IPermitTypeField> {
    const map = new Map()
    this.activeFormType.workflowSteps.forEach(workflowStep => {
      workflowStep.fields.forEach(f => map.set(f.id, f))
      workflowStep.conditionalFields.forEach(condField => {
        condField.values.forEach(f => map.set(f.id, f))
      })
    })

    return map
  }

  @computed
  private get formReportPermitsMap(): Map<string, Workflow> {
    return new Map(this.reportsStore.allFormReportPermits.map(f => [f.id, f]))
  }

  @computed
  private get formFieldMapsCache(): Map<
    string,
    {
      fieldMap: Map<string, PermitField>
      dependentFieldMap: Map<string, PermitField>
    }
  > {
    const result = new Map<
      string,
      {
        fieldMap: Map<string, PermitField>
        dependentFieldMap: Map<string, PermitField>
      }
    >()

    for (const form of this.reportsStore.allFormReportPermits) {
      const fieldMap = new Map(form.fields.map(f => [f.fieldId, f]))
      const dependentFieldMap = new Map<string, PermitField>()

      for (const f of form.fields) {
        if (f.parentInstanceId && f.parentValueIndex !== undefined) {
          const key = `${f.fieldId}_${f.parentInstanceId}_${f.parentValueIndex}`
          dependentFieldMap.set(key, f)
        }
      }

      result.set(form.id, { fieldMap, dependentFieldMap })
    }

    return result
  }

  public getRowHeight = ({ index }): number => {
    const row = this.rows[index]

    if (row?.category) return CATEGORY_ROW_HEIGHT

    const responsibleIds = row.data && row.data[DataKeys.RESPONSIBLE]

    if (responsibleIds?.length <= 1) {
      return DEFAULT_ROW_HEIGHT
    }

    return responsibleIds?.length * MULTILINE_ROW_HEIGHT
  }

  public columnsWidthState = new Map<string, number>([
    [DataKeys.CHECKBOX, 60],
    [DataKeys.NAME, 200],
    [DataKeys.RECEIPT, 150],
    [DataKeys.STATUS, 200],
    [DataKeys.LOCATION, 250],
    [DataKeys.START_DATE, 250],
    [DataKeys.END_DATE, 250],
    [DataKeys.COMPANY, 250],
    [DataKeys.RESPONSIBLE, 250],
  ])

  @computed
  public get columns(): ILWFCColumn[] {
    const columns = [
      {
        dataKey: DataKeys.CHECKBOX,
      },
      {
        label: Localization.translator.name,
        dataKey: DataKeys.NAME,
      },
      {
        label: Localization.translator.receipt,
        dataKey: DataKeys.RECEIPT,
      },
      {
        label: Localization.translator.status,
        dataKey: DataKeys.STATUS,
      },
      {
        label: Localization.translator.location,
        dataKey: DataKeys.LOCATION,
      },
      {
        label: Localization.translator.startDate,
        dataKey: DataKeys.START_DATE,
      },
      {
        label: Localization.translator.finishDate,
        dataKey: DataKeys.END_DATE,
      },
      {
        label: Localization.translator.company,
        dataKey: DataKeys.COMPANY,
      },
      {
        label: Localization.translator.responsible,
        dataKey: DataKeys.RESPONSIBLE,
      },
      ...this.dynamicColumns.map(dynamicColumn => ({
        label: dynamicColumn.label,
        dataKey: dynamicColumn.uiKey,
      })),
    ]

    return columns.map(column => ({
      ...column,
      width: this.getColumnWidth(column),
    }))
  }

  private getColumnWidth(column: any): number {
    if (this.columnsWidthState.has(column.dataKey)) {
      return this.columnsWidthState.get(column.dataKey)
    }

    if (column?.label) {
      const calculatedWidth =
        Math.round(column.label.length / SMALL_DYNAMIC_COLUMN_MIN_LENGTH) *
        BIG_DYNAMIC_COLUMN_WIDTH_MULTIPLIER
      return Math.max(SMALL_DYNAMIC_COLUMN_WIDTH, calculatedWidth)
    }
    return null
  }

  @computed
  public get dynamicColumns(): DynamicColumn[] {
    const columns: DynamicColumn[] = []
    const { permitTypeFields, summaryFields } =
      this.reportsStore.activeReportView || {}

    if (permitTypeFields?.length) {
      permitTypeFields.forEach(permitTypeField => {
        const field = this.activeFormTypeFieldsMap.get(
          permitTypeField.permitTypeFieldId,
        )

        if (!field) return

        const parentField = this.findParentField(
          permitTypeField.permitTypeFieldId,
        )
        const isDependentField = !!parentField

        const isExpandableType =
          field.type === PermitFieldType.Table ||
          materialPermitFieldTypes.includes(field.type) ||
          materialResourcePermitFieldTypes.includes(field.type)

        const label = permitTypeField.checkListItemId
          ? field.checklist.list.find(
              item => item.id === permitTypeField.checkListItemId,
            )?.text
          : field.caption

        const uiKey = permitTypeField.checkListItemId
          ? `${field.id}_${permitTypeField.checkListItemId}`
          : field.id

        columns.push({
          label,
          dataKey: field.id,
          uiKey,
          checkListItemId: permitTypeField.checkListItemId,
          isDependentField,
          parentFieldId: parentField?.id,
          isParentField: isExpandableType,
        })
      })

      // Ensure parent columns appear before their dependent columns
      const ordered: DynamicColumn[] = []
      const dependentsByParent = new Map<string, DynamicColumn[]>()
      for (const col of columns) {
        if (col.isDependentField && col.parentFieldId) {
          const deps = dependentsByParent.get(col.parentFieldId) || []
          deps.push(col)
          dependentsByParent.set(col.parentFieldId, deps)
        }
      }
      const placed = new Set<string>()
      for (const col of columns) {
        if (placed.has(col.uiKey)) continue
        // Skip dependents — they'll be placed after their parent
        if (col.isDependentField && col.parentFieldId) continue
        ordered.push(col)
        placed.add(col.uiKey)
        const deps = dependentsByParent.get(col.dataKey)
        if (deps) {
          for (const dep of deps) {
            if (!placed.has(dep.uiKey)) {
              ordered.push(dep)
              placed.add(dep.uiKey)
            }
          }
        }
      }
      columns.length = 0
      columns.push(...ordered)
    }

    if (summaryFields?.length) {
      summaryFields.forEach(summaryField => {
        const dataKey = `${DataKeys.SUMMARIZED}_${summaryField.fieldId}`
        columns.push({
          label: summaryField.name,
          dataKey,
          uiKey: dataKey,
          isSummarized: true,
        })
      })
    }

    return columns
  }

  private findParentField(fieldId: string): IPermitTypeField | undefined {
    const field = this.activeFormTypeFieldsMap.get(fieldId)

    if (field?.parentId) {
      const parentField = this.activeFormTypeFieldsMap.get(field.parentId)
      const isExpandableParent =
        parentField?.type === PermitFieldType.Table ||
        materialPermitFieldTypes.includes(parentField?.type) ||
        materialResourcePermitFieldTypes.includes(parentField?.type)
      if (isExpandableParent) {
        return parentField
      }
    }

    // Check conditional fields for table/material dependencies
    if (!this.activeFormType?.workflowSteps) {
      return undefined
    }

    for (const workflowStep of this.activeFormType.workflowSteps) {
      for (const condField of workflowStep.conditionalFields || []) {
        if (
          condField.key !== PERMIT_TABLE_FIELDS_KEY &&
          condField.key !== FORM_MATERIAL_FIELDS_KEY &&
          condField.key !== FORM_MATERIAL_RESOURCE_FIELDS_KEY &&
          condField.key !== FORM_MATERIAL_RESOURCE_ASSOCIATED_FIELDS_KEY
        ) {
          continue
        }

        const isDependent = condField.values?.some(v => v.id === fieldId)
        if (isDependent) {
          return this.activeFormTypeFieldsMap.get(condField.fieldId)
        }
      }
    }

    return undefined
  }

  @action.bound
  public onRowClick(rowData: LWFCRowData, columnKey: string) {
    const id = rowData[DataKeys.ID]

    switch (columnKey) {
      case DataKeys.CHECKBOX:
        this.toggleInstance(id)
        return
      default:
        const item: Workflow = this.reportsStore.allFormReportPermits.find(
          form => form.id === id,
        )
        this.reportsStore.showFormReports(item)
    }
  }

  public get rows(): ILWFCRow[] {
    const { groupingKey } = this.state.formReportFilters

    const keys = []
    keys.push(groupingKey)

    let bandRows = this.toBandTreeNodeRows(
      keys,
      null,
      '',
      0,
      false,
      this.sortTreeObjs,
    )

    if (this.hasDynamicRecurringField) {
      bandRows = this.populateWithInspections(bandRows)
    }

    if (this.hasDynamicDependentField) {
      bandRows = this.populateWithDependentRows(bandRows)
    }

    return bandRows
  }

  private sortTreeObjs = (treeNodeObjs: ITreeNodeObj[]) => {
    const map = new Map<string, ITreeNodeObj>()

    treeNodeObjs.forEach(obj => map.set(obj.id, obj))

    return Array.from(map.values()).sort((a, b) => {
      if (unassignedValues.includes(a.id)) {
        return 1
      }
      if (unassignedValues.includes(b.id)) {
        return -1
      }

      return this.collator.compare(a.name, b.name)
    })
  }

  private get hasDynamicRecurringField() {
    const recurringStep = this.activeFormType.workflowSteps.find(
      ws => ws.type === WorkflowStepType.RecurringInspection,
    )

    if (!recurringStep) return

    const recurringFields = [
      ...recurringStep.fields,
      ...recurringStep.conditionalFields.flatMap(cf => cf.values),
    ]

    return this.dynamicColumns.some(column =>
      recurringFields.find(f => f.id === column.dataKey),
    )
  }

  private get hasDynamicDependentField() {
    return this.dependentColumnsConfig.parentFieldIds.length > 0
  }

  @computed
  private get dependentColumnsConfig() {
    const parentFieldIds = new Set<string>()
    const parentColumnsByFieldId = new Map<string, DynamicColumn>()
    const dependentColumnsByParent = new Map<string, DynamicColumn[]>()

    for (const col of this.dynamicColumns) {
      if (col.isSummarized) continue

      if (col.isParentField) {
        parentFieldIds.add(col.dataKey)
        parentColumnsByFieldId.set(col.dataKey, col)
      }

      if (col.isDependentField && col.parentFieldId) {
        parentFieldIds.add(col.parentFieldId)
        const existing = dependentColumnsByParent.get(col.parentFieldId) || []
        existing.push(col)
        dependentColumnsByParent.set(col.parentFieldId, existing)
      }
    }

    return {
      parentFieldIds: [...parentFieldIds],
      parentColumnsByFieldId,
      dependentColumnsByParent,
    }
  }

  private formatSubRowName(
    baseName: string,
    index: number,
    prefix: string = 'Row',
  ): { isDependentRow?: boolean; isInspection?: boolean; value: string } {
    return {
      isDependentRow: prefix === 'Row',
      isInspection: prefix !== 'Row',
      value: baseName.replace(
        ':',
        `.${prefix === 'Row' ? `Row${index}` : index}:`,
      ),
    }
  }

  private populateWithDependentRows = (rows: ILWFCRow[]): ILWFCRow[] => {
    const { parentFieldIds, parentColumnsByFieldId, dependentColumnsByParent } =
      this.dependentColumnsConfig

    if (!parentFieldIds.length) return rows

    const dateFormatter =
      this.projectDateStore.getWeekdayMonthDayYearAndTimeToDisplay

    const resultRows: ILWFCRow[] = []

    for (const row of rows) {
      resultRows.push(row)

      if (!this.shouldExpandRow(row)) continue

      const formId = row.data[DataKeys.ID]
      const form = this.formReportPermitsMap.get(formId)
      if (!form) continue

      const fieldMaps = this.formFieldMapsCache.get(formId)
      if (!fieldMaps) continue

      const parentFieldsWithData = this.getParentFieldsWithData(
        parentFieldIds,
        fieldMaps.fieldMap,
      )
      if (!parentFieldsWithData.length) continue

      // Row-0 is inlined into the main row during toRows (applyInlineParentRow);
      // here we emit only the remaining rows (Row2, Row3, ...), so a
      // single-row/value field produces no sub-row. STX1-569.
      const extraRows = this.createExpandedRows(
        row,
        parentFieldsWithData,
        parentColumnsByFieldId,
        dependentColumnsByParent,
        dateFormatter,
        fieldMaps.dependentFieldMap,
        1,
      )
      resultRows.push(...extraRows)
    }

    return resultRows
  }

  private shouldExpandRow(row: ILWFCRow): boolean {
    if (row.category) return false

    const nameData = row.data[DataKeys.NAME]
    return !(typeof nameData === 'object' && nameData?.isInspection)
  }

  private createExpandedRows(
    row: ILWFCRow,
    parentFieldsWithData: ParentFieldWithData[],
    parentColumnsByFieldId: Map<string, DynamicColumn>,
    dependentColumnsByParent: Map<string, DynamicColumn[]>,
    dateFormatter: (date: Date | number) => string,
    dependentFieldMap: Map<string, PermitField>,
    startIndex: number = 0,
  ): ILWFCRow[] {
    const maxRows = Math.max(
      ...parentFieldsWithData.map(p => p.parentField.values.length),
    )

    const baseRowData = this.createBaseRowData(row)
    const expandedRows: ILWFCRow[] = []

    for (let rowIndex = startIndex; rowIndex < maxRows; rowIndex++) {
      const rowData = this.createDependentRowData(
        row,
        rowIndex,
        baseRowData,
        parentFieldsWithData,
        parentColumnsByFieldId,
        dependentColumnsByParent,
        dateFormatter,
        dependentFieldMap,
      )

      if (rowData) {
        expandedRows.push({ data: rowData })
      }
    }

    return expandedRows
  }

  private getParentFieldsWithData(
    parentFieldIds: string[],
    fieldMap: Map<string, PermitField>,
  ): ParentFieldWithData[] {
    const result: ParentFieldWithData[] = []

    for (const parentFieldId of parentFieldIds) {
      const parentField = fieldMap.get(parentFieldId)
      if (!parentField?.values?.length) continue

      result.push({
        parentFieldId,
        parentField,
        isTable: parentField.type === PermitFieldType.Table,
      })
    }

    return result
  }

  private createBaseRowData(row: ILWFCRow): LWFCRowData {
    return {
      [DataKeys.ID]: row.data[DataKeys.ID],
      [DataKeys.CHECKBOX]: row.data[DataKeys.CHECKBOX],
      [DataKeys.RECEIPT]: Localization.translator.log,
      [DataKeys.STATUS]: row.data[DataKeys.STATUS],
      [DataKeys.COMPANY]: row.data[DataKeys.COMPANY],
      [DataKeys.LOCATION]: row.data[DataKeys.LOCATION],
      [DataKeys.RESPONSIBLE]: row.data[DataKeys.RESPONSIBLE],
      [DataKeys.START_DATE]: row.data[DataKeys.START_DATE],
      [DataKeys.END_DATE]: row.data[DataKeys.END_DATE],
    }
  }

  private createDependentRowData(
    row: ILWFCRow,
    rowIndex: number,
    baseRowData: LWFCRowData,
    parentFieldsWithData: ParentFieldWithData[],
    parentColumnsByFieldId: Map<string, DynamicColumn>,
    dependentColumnsByParent: Map<string, DynamicColumn[]>,
    dateFormatter: (date: Date | number) => string,
    dependentFieldMap: Map<string, PermitField>,
  ): LWFCRowData | null {
    const data: LWFCRowData = {
      ...baseRowData,
      [DataKeys.NAME]: this.formatSubRowName(
        row.data[DataKeys.NAME],
        rowIndex + 1,
      ),
    }

    const hasData = this.applyParentFieldsToRow(
      data,
      rowIndex,
      parentFieldsWithData,
      parentColumnsByFieldId,
      dependentColumnsByParent,
      dateFormatter,
      dependentFieldMap,
    )

    return hasData ? data : null
  }

  /**
   * Writes row `rowIndex`'s values into `data`. Used both for sub-rows and for
   * inlining a single-row/value field into the main row (rowIndex 0). Returns
   * whether any value was written.
   */
  private applyParentFieldsToRow(
    data: LWFCRowData,
    rowIndex: number,
    parentFieldsWithData: ParentFieldWithData[],
    parentColumnsByFieldId: Map<string, DynamicColumn>,
    dependentColumnsByParent: Map<string, DynamicColumn[]>,
    dateFormatter: (date: Date | number) => string,
    dependentFieldMap: Map<string, PermitField>,
  ): boolean {
    let hasData = false

    for (const {
      parentFieldId,
      parentField,
      isTable,
    } of parentFieldsWithData) {
      const valueAtIndex = parentField.values[rowIndex]
      if (!valueAtIndex) continue

      // Process parent column
      const parentColumn = parentColumnsByFieldId.get(parentFieldId)
      if (parentColumn) {
        hasData = true
        if (!isTable) {
          data[parentColumn.uiKey] = this.formatFieldValue(
            { ...parentField, values: [valueAtIndex] },
            parentColumn,
            dateFormatter,
          )
        }
      }

      // Process dependent columns
      const dependentColumns = dependentColumnsByParent.get(parentFieldId)
      if (!dependentColumns?.length) continue

      if (isTable) {
        this.populateTableDependentColumns(
          data,
          valueAtIndex,
          dependentColumns,
          dateFormatter,
        )
        hasData = true
      } else {
        const populated = this.populateMaterialDependentColumns(
          data,
          dependentFieldMap,
          parentField.instanceId,
          rowIndex,
          dependentColumns,
          dateFormatter,
        )
        if (populated) hasData = true
      }
    }

    return hasData
  }

  private populateTableDependentColumns(
    data: LWFCRowData,
    tableRowValue: unknown,
    dependentColumns: DynamicColumn[],
    dateFormatter: (date: Date | number) => string,
  ): void {
    if (!Array.isArray(tableRowValue) || !tableRowValue.length) return

    const firstItem = tableRowValue[0]
    if (
      !firstItem ||
      typeof firstItem !== 'object' ||
      !('fieldId' in firstItem)
    ) {
      return
    }

    const cellMap = new Map(
      (tableRowValue as PermitField[]).map(c => [c.fieldId, c]),
    )

    for (const col of dependentColumns) {
      const cell = cellMap.get(col.dataKey)
      if (!cell) continue

      data[col.uiKey] = this.formatFieldValue(cell, col, dateFormatter)
    }
  }

  private populateMaterialDependentColumns(
    data: LWFCRowData,
    dependentFieldMap: Map<string, PermitField>,
    parentInstanceId: string,
    rowIndex: number,
    dependentColumns: DynamicColumn[],
    dateFormatter: (date: Date | number) => string,
  ): boolean {
    let populated = false

    for (const col of dependentColumns) {
      const key = `${col.dataKey}_${parentInstanceId}_${rowIndex}`
      const dependentField = dependentFieldMap.get(key)
      if (!dependentField) continue

      populated = true
      data[col.uiKey] = this.formatFieldValue(
        dependentField,
        col,
        dateFormatter,
      )
    }

    return populated
  }

  private formatFieldValue(
    field: PermitField,
    column: DynamicColumn,
    dateFormatter: (date: Date | number) => string,
  ): PermitField {
    const formattedValues = this.getFormattedFieldValues(
      field,
      column,
      dateFormatter,
    )
    return { ...field, values: formattedValues }
  }

  private populateWithInspections = (rows: ILWFCRow[]) => {
    const { getWeekdayMonthDayYearAndTimeToDisplay } = this.projectDateStore
    const permitWithInspections = []

    rows.forEach(row => {
      if (row.category) {
        permitWithInspections.push(row)
        return
      }

      const permitId = row.data[DataKeys.ID]

      permitWithInspections.push(row)

      const inspections =
        this.permitInspectionsStore.getInspectionsForPermit(permitId)

      if (!inspections?.length) return

      inspections.forEach((insp, i) => {
        let inspectionHaveDynamicField = false

        const latestChange =
          this.permitInspectionChangesStore.getLatestChangeForInspection(
            insp.id,
          )

        const inspection = latestChange
          ? latestChange.toInspection(permitId)
          : insp

        const data = {
          [DataKeys.ID]: row.data[DataKeys.ID],
          [DataKeys.CHECKBOX]: row.data[DataKeys.CHECKBOX],
          [DataKeys.RECEIPT]: Localization.translator.log,
          [DataKeys.STATUS]: row.data[DataKeys.STATUS],
          [DataKeys.COMPANY]: row.data[DataKeys.COMPANY],
          [DataKeys.LOCATION]: row.data[DataKeys.LOCATION],
          [DataKeys.RESPONSIBLE]: row.data[DataKeys.RESPONSIBLE],
          [DataKeys.NAME]: this.formatSubRowName(
            row.data[DataKeys.NAME],
            i + 1,
            '',
          ),
          [DataKeys.START_DATE]: getWeekdayMonthDayYearAndTimeToDisplay(
            inspection.inspectionDate,
          ),
          [DataKeys.END_DATE]: getWeekdayMonthDayYearAndTimeToDisplay(
            inspection.inspectionDate,
          ),
        }

        this.dynamicColumns.forEach(dynamicColumn => {
          if (dynamicColumn.isSummarized) return

          const fields = inspection.fields.filter(
            f => f.fieldId === dynamicColumn.dataKey,
          )
          if (!fields?.length) return

          inspectionHaveDynamicField = true

          const mergedField: PermitField = {
            ...copyObjectDeep(fields[0]),
            values: fields.flatMap(f => f.values),
          }
          mergedField.values = this.getFormattedFieldValues(
            mergedField,
            dynamicColumn,
            getWeekdayMonthDayYearAndTimeToDisplay,
          )

          data[dynamicColumn.uiKey] = mergedField
        })
        if (inspectionHaveDynamicField) {
          permitWithInspections.push({
            data,
          })
        }
      })
    })

    return permitWithInspections
  }

  protected get instancesInPeriodInterval(): Workflow[] {
    return this.reportsStore.formReportPermitsInPeriodInterval
  }

  protected searchFiltering = (form: Workflow) => {
    const key = this.filter.searchKey.toLowerCase()
    const formLocationNames = form.locations.reduce((set, loc) => {
      const formLocation = this.locationAttributesStore.getById(loc.id)
      if (formLocation?.name) {
        set.add(formLocation?.name)
      }
      return set
    }, new Set<string>())

    const entityValues: string[] = [
      ...Array.from(formLocationNames),
      form.getCaption(this.workflowTypesStore),
      this.projectDateStore.getWeekdayMonthDayAndTimeToDisplay(form.startDate),
      this.projectDateStore.getWeekdayMonthDayAndTimeToDisplay(form.endDate),
      form.status,
      getFormStatusDisplayName(form.status, form.workflowStepLevel),
      ...this.projectMembersStore
        .getByIds(form.responsibleIds)
        .map(u => u.fullName),
      ...form.companyIds.map(id => this.getCompanyName(id)),
    ]
    return entityValues.some(value => value?.toLowerCase().includes(key))
  }

  @computed
  protected get categoryToInstancesMap() {
    const map = {}

    this.filteredCollection.forEach(pair => {
      const categoryIds = this.getCategoryIds(pair, this.groupingKey) || [
        UNASSIGNED,
      ]

      categoryIds.forEach(id => {
        const formattedCategoryId = id || UNASSIGNED

        if (map[formattedCategoryId]) {
          map[formattedCategoryId].push(pair)
        } else {
          map[formattedCategoryId] = [pair]
        }
      })
    })

    return map
  }

  public getCategoryIds(form: Workflow, band: string): string[] {
    const startDate = form?.startDate
    const { getWeekRange, getMonthRange, getMonthDayAndYearToDisplay } =
      this.projectDateStore
    switch (band) {
      case FormReportGroupingOption.COMPANY:
        return form.companyIds?.length ? form.companyIds : [UNASSIGNED]
      case FormReportGroupingOption.DAY:
        return [getMonthDayAndYearToDisplay(startDate) || UNASSIGNED]
      case FormReportGroupingOption.WEEK:
        return [getWeekRange(startDate) || UNASSIGNED]
      case FormReportGroupingOption.MONTH:
        return [getMonthRange(startDate) || UNASSIGNED]
      case FormReportGroupingOption.NONE:
        return [FormReportGroupingOption.NONE]
      default:
        return []
    }
  }

  public get reportInfo() {
    const {
      formReportFilters: { fieldsMap },
      activeProject: { dateTimeFormat, code, materialsUploadId },
      user: { id: userId },
    } = this.state
    const { order, columnKey } = this.sortState
    const { startDate, endDate } = this.reportsStore

    const filters = this.getPermitFilters(fieldsMap)
    return {
      userId,
      materialsUploadId,
      projectTimeZone: this.projectDateStore.getClientTimezoneId(),
      dateTimeFormat,
      permitsFrom: startDate.getTime(),
      permitsTo: endDate.getTime(),
      filteredIds: {
        permits: this.filteredCollection.map(form => form.id),
      },
      linkedWorkflowIds: this.filteredCollection.flatMap(
        form => form.linkedWorkflowFields,
      ),
      filters,
      grouping: {
        permits: this.groupingKey,
      },
      sorting: {
        permits: [
          {
            ascending: order !== SortOrder.DESC,
            key: columnKey || DEFAULT_SORT_KEY,
          },
        ],
      },
      qrCodeLinks: this.filteredCollection.map(form =>
        Workflow.getDirectLinkToInstance(code, form.id),
      ),
      dynamicColumns: this.dynamicColumns,
    }
  }

  private getPermitFilters(fieldsMap: { [filterType: string]: UIFilterInfo }): {
    [filterType: string]: string[]
  } {
    const result: { [filterType: string]: string[] } = {}

    Object.entries(fieldsMap).forEach(([filterType, filter]) => {
      switch (filterType) {
        case AcceptableFormFilterTypes.COMPANY:
          result[filterType] = Array.from(
            filter.selectedFilterOptions.keys(),
          ).map(id => this.getCompanyName(id))
          break
        case AcceptableFormFilterTypes.STATUS:
          result[filterType] = Array.from(filter.selectedFilterOptions.keys())
          break
        case AcceptableFormFilterTypes.LOCATION:
          result[filterType] = Array.from(
            filter.selectedFilterOptions.keys(),
          ).reduce((list, key) => {
            if (key === UNASSIGNED) {
              return list
            }

            const attributeId = key.split(LOCATION_SEPARATOR)[1]
            const location = this.locationAttributesStore.getById(attributeId)
            if (location?.name) list.push(location.name)

            return list
          }, [] as string[])
          break
      }
    })

    return result
  }

  @action.bound
  public selectRow(objectId: string) {
    this.resetSelection()
    this.selection.set(objectId, true)
  }

  protected compareRows = (
    { data: aRowData }: ILWFCRow,
    { data: bRowData }: ILWFCRow,
  ): number => {
    const { columnKey, order } = this.sortState

    const v1 = this.getSortingValue(aRowData, columnKey)
    const v2 = this.getSortingValue(bRowData, columnKey)

    if (typeof v1 === 'string' && typeof v2 === 'string') {
      if (unassignedValues.includes(v1)) {
        return 1
      }
      if (unassignedValues.includes(v2)) {
        return -1
      }
      return this.collator.compare(v1, v2) * order
    }

    if (typeof v1 === 'number' && typeof v2 === 'number') {
      return (v1 - v2) * order
    }

    return 0
  }

  private getSortingValue(rowData: LWFCRowData, columnKey: string) {
    const isDynamicColumn = this.dynamicColumns.find(c => c.uiKey === columnKey)

    if (isDynamicColumn) {
      return this.getDynamicColumnSortingValue(rowData[columnKey])
    }

    const form = this.filteredCollection.find(
      f => f.id === rowData[DataKeys.ID],
    )
    switch (columnKey) {
      case DataKeys.START_DATE:
        return form.startDate
      case DataKeys.END_DATE:
        return form.endDate
      case DataKeys.STATUS:
        return getFormStatusDisplayName(form.status, form.workflowStepLevel)
      case DataKeys.LOCATION:
        const locations = rowData[columnKey]
        return locations?.length > 1
          ? locations.length.toString()
          : locations?.[0]?.name || EMPTY_STRING
      case DataKeys.COMPANY:
        const companiesData = rowData[columnKey]
        return companiesData?.length > 1
          ? companiesData.length.toString()
          : companiesData?.[0]?.name || EMPTY_STRING
      case DataKeys.RESPONSIBLE:
        const usersData = rowData[columnKey]
        return usersData?.length > 1
          ? usersData.length.toString()
          : usersData?.[0]?.fullName || EMPTY_STRING

      default:
        const value = rowData[columnKey]
        return value === NO_SPECIFIED ? EMPTY_STRING : value
    }
  }

  private getDynamicColumnSortingValue(field: any): string {
    if (!field) {
      return EMPTY_STRING
    }

    switch (true) {
      case locationPermitFieldTypes.includes(field.type):
      case companyPermitFieldTypes.includes(field.type):
      case materialPermitFieldTypes.includes(field.type):
      case materialResourcePermitFieldTypes.includes(field.type):
        return field.values?.length > 1
          ? field.values.length.toString()
          : field.values?.[0]?.name || EMPTY_STRING
      case userPermitFieldTypes.includes(field.type):
        return field.values?.length > 1
          ? field.values.length.toString()
          : field.values?.[0]?.fullName || EMPTY_STRING

      default:
        return field.values?.join(' ') || EMPTY_STRING
    }
  }

  protected getTreeNodeObjsByBand(currentBand: string): ITreeNodeObj[] {
    const { getMonthDayAndYearToDisplay, getWeekRange, getMonthRange } =
      this.projectDateStore

    switch (currentBand) {
      case FormReportGroupingOption.NONE:
        return [groupingCommonMapper(FormReportGroupingOption.NONE)]
      case FormReportGroupingOption.DAY:
        return this.filteredCollection
          .map(
            form => getMonthDayAndYearToDisplay(form.startDate) || UNASSIGNED,
          )
          .map(groupingCommonMapper)
      case FormReportGroupingOption.WEEK:
        return this.filteredCollection
          .map(form => getWeekRange(form.startDate) || UNASSIGNED)
          .map(groupingCommonMapper)
      case FormReportGroupingOption.MONTH:
        return this.filteredCollection
          .map(form => getMonthRange(form.startDate) || UNASSIGNED)
          .map(groupingCommonMapper)
      case FormReportGroupingOption.COMPANY:
        return [
          ...this.filteredCollection
            .flatMap(form => form.companyIds)
            .map(companyId => {
              const company = this.companiesStore.getCompanyById(companyId)
              return {
                name: company?.formattedName || UNASSIGNED,
                id: company?.id || UNASSIGNED,
              }
            }),
          groupingCommonMapper(UNASSIGNED),
        ]
      default:
        return []
    }
  }

  @computed
  protected get bandObjectMap(): {
    [bandType: string]: { [groupName: string]: Workflow[] }
  } {
    const map = {}

    const groupingOptions = Object.values(FormReportGroupingOption)

    groupingOptions.forEach(band => {
      const bandMap = (map[band] = {})

      this.filteredCollection.forEach(report => {
        const groupNames: string[] = this.getCategoryIds(report, band) || [
          UNASSIGNED,
        ]

        groupNames.forEach(group => {
          const groupName = group ? group : UNASSIGNED

          if (!bandMap[groupName]) {
            bandMap[groupName] = []
          }
          bandMap[groupName].push(report)
        })
      })
    })

    return map
  }

  protected toRows(reports: Workflow[]) {
    const { getWeekdayMonthDayYearAndTimeToDisplay } = this.projectDateStore

    const hasSummaryColumn = this.dynamicColumns.some(
      column => column.isSummarized,
    )

    return reports.map(form => {
      const data = {
        [DataKeys.ID]: form.id,
        [DataKeys.CHECKBOX]: this.selection.get(form.id),
        [DataKeys.RECEIPT]: hasSummaryColumn
          ? Localization.translator.summary
          : NO_VALUE,
        [DataKeys.NAME]: form.getCaption(this.workflowTypesStore),
        [DataKeys.STATUS]: {
          status: form.status,
          workflowStepLevel: form.workflowStepLevel,
          isLate: form.isLate,
        },
        [DataKeys.START_DATE]: getWeekdayMonthDayYearAndTimeToDisplay(
          form.startDate,
        ),
        [DataKeys.END_DATE]: getWeekdayMonthDayYearAndTimeToDisplay(
          form.endDate,
        ),
        [DataKeys.COMPANY]: form.companyIds
          ?.map(id => this.companiesStore.getCompanyById(id))
          .filter(c => c),
        [DataKeys.LOCATION]:
          form.locations.reduce((list, loc) => {
            const formLocation = this.locationAttributesStore.getById(loc.id)
            if (formLocation) {
              list.push(formLocation)
            }
            return list
          }, []) || [],
        [DataKeys.RESPONSIBLE]: this.projectMembersStore.getByIds(
          form.responsibleIds,
        ),
      }

      const inspections = this.permitInspectionsStore.getInspectionsForPermit(
        form.id,
      )

      const formInspectionFields = inspections
        .flatMap(insp => {
          const latestChange =
            this.permitInspectionChangesStore.getLatestChangeForInspection(
              insp.id,
            )

          const inspection = latestChange
            ? latestChange.toInspection(form.id)
            : insp

          return inspection.fields
        })
        .reduce((acc, f) => {
          const field = acc.find(accField => accField.fieldId === f.fieldId)
          if (field) {
            field.values.push(...f.values)
          } else {
            acc.push(copyObjectDeep(f))
          }
          return acc
        }, [] as PermitField[])

      this.dynamicColumns.forEach(dynamicColumn => {
        if (dynamicColumn.isDependentField) {
          return
        }

        const fields: PermitField[] = dynamicColumn.isSummarized
          ? [...form.fields, ...formInspectionFields]
          : form.fields

        const formattedDataKey = dynamicColumn.dataKey.replace(
          `${DataKeys.SUMMARIZED}_`,
          '',
        )
        const relatedFields = fields.filter(f => f.fieldId === formattedDataKey)

        if (!relatedFields?.length) return

        const mergedField: PermitField = {
          ...copyObjectDeep(relatedFields[0]),
          values: relatedFields.flatMap(f => f.values),
        }
        mergedField.values = this.getFormattedFieldValues(
          mergedField,
          dynamicColumn,
          getWeekdayMonthDayYearAndTimeToDisplay,
        )

        if (dynamicColumn.isSummarized && mergedField.values.length) {
          mergedField.values = [
            mergedField.values.reduce(
              (acc, value) => (acc += parseInt(value, 10)),
              0,
            ),
          ]
        }

        data[dynamicColumn.uiKey] = mergedField
      })

      // Inline each expandable field's row-0 value here, before the band-tree
      // sort runs in toBandTreeNodeRows. Doing it later (in
      // populateWithDependentRows) left these columns empty at compareRows
      // time, so sorting by an inlined table/material column was a no-op.
      this.applyInlineParentRow(data, form)

      return { data }
    })
  }

  /**
   * Writes the row-0 value of every expandable (table/material) parent field
   * and its dependent columns into the permit's main-row data. Mirrors what
   * populateWithDependentRows does for sub-rows, but runs during toRows so the
   * inlined values exist before sorting. STX1-569.
   */
  private applyInlineParentRow(data: LWFCRowData, form: Workflow): void {
    const { parentFieldIds, parentColumnsByFieldId, dependentColumnsByParent } =
      this.dependentColumnsConfig

    if (!parentFieldIds.length) return

    const fieldMaps = this.formFieldMapsCache.get(form.id)
    if (!fieldMaps) return

    const parentFieldsWithData = this.getParentFieldsWithData(
      parentFieldIds,
      fieldMaps.fieldMap,
    )
    if (!parentFieldsWithData.length) return

    this.applyParentFieldsToRow(
      data,
      0,
      parentFieldsWithData,
      parentColumnsByFieldId,
      dependentColumnsByParent,
      this.projectDateStore.getWeekdayMonthDayYearAndTimeToDisplay,
      fieldMaps.dependentFieldMap,
    )
  }

  private getCompanyName = (companyId: string): string => {
    return this.companiesStore.getCompanyNameById(companyId)
  }

  private getFormattedFieldValues = (
    field: PermitField,
    dynamicColumn: DynamicColumn,
    dateFormatter: (date: Date | number) => string,
  ): any[] => {
    if (locationPermitFieldTypes.includes(field.type)) {
      return field.values?.reduce((list, loc) => {
        const formLocation = this.locationAttributesStore.getById(loc.id)
        if (formLocation) {
          list.push(formLocation)
        }
        return list
      }, [])
    }
    if (userPermitFieldTypes.includes(field.type)) {
      return this.projectMembersStore.getByIds(field.values)
    }
    if (companyPermitFieldTypes.includes(field.type)) {
      return field.values
        .map(id => this.companiesStore.getCompanyById(id))
        .filter(c => c)
    }
    if (materialPermitFieldTypes.includes(field.type)) {
      const materialIds =
        field.values?.map(v => v.materialId)?.filter(Boolean) ?? []
      return this.materialsStore.getByIds(materialIds)
    }
    if (materialResourcePermitFieldTypes.includes(field.type)) {
      return (
        field.values
          ?.map(v =>
            this.materialResourceRecordsById.get(String(v?.locationId)),
          )
          ?.filter(Boolean) ?? []
      )
    }
    if (isLinkedWorkflowLikeField(field.type)) {
      return (
        field.values
          ?.map(id => {
            const workflow = this.reportsStore.workflows?.find(w => w.id === id)
            return workflow?.getCaption(this.workflowTypesStore)
          })
          ?.filter(Boolean) ?? []
      )
    }
    if (field.type === PermitFieldType.AdditionalDate) {
      return field.values.map(value => dateFormatter(value))
    }
    if (checklistFormFieldTypes.includes(field.type)) {
      return (
        field.values
          ?.filter(v => v.checklistItemId === dynamicColumn.checkListItemId)
          ?.map(v => {
            switch (true) {
              case v.answer === undefined:
                return NO_VALUE
              case v.answer === null:
                return Localization.translator.NA
              case v.answer:
                return Localization.translator.yes
              case !v.answer:
                return Localization.translator.no
            }
          }) ?? []
      )
    }
    return field.values
  }
}
